/**
 * Staff API Module
 * All staff-related API endpoints
 */

import { api } from './client.js';

/**
 * Staff API endpoints
 */
export const staffApi = {
  /**
   * Get all staff with optional filters and pagination
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.limit - Items per page (default: 10, max: 100)
   * @param {string} params.status - Filter by status (active, inactive)
   * @param {string} params.role - Filter by role (admin, manager, staff)
   * @param {string} params.search - Search term for name, email, or department
   * @returns {Promise<Object>} Staff data with pagination
   */
  getAll: (params = {}) => {
    const cleanParams = Object.entries(params).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {});

    const queryParams = new URLSearchParams(cleanParams).toString();
    const endpoint = `/admin/staff${queryParams ? `?${queryParams}` : ''}`;
    return api.get(endpoint);
  },

  /**
   * Get staff by ID
   * @param {string|number} id - Staff ID
   * @returns {Promise<Object>} Staff data
   */
  getById: (id) => {
    if (!id) {
      return Promise.reject(new Error('Staff ID is required'));
    }
    return api.get(`/admin/staff/${id}`);
  },

  /**
   * Create new staff
   * @param {Object} staffData - Staff data
   * @param {string} staffData.name - Staff name (required)
   * @param {string} staffData.email - Email (required)
   * @param {string} staffData.password - Password (required)
   * @param {string} [staffData.phone] - Phone number
   * @param {string} [staffData.role] - Role (admin, manager, staff)
   * @param {string} [staffData.department] - Department
   * @param {string} [staffData.status] - Status (active, inactive)
   * @returns {Promise<Object>} Created staff data
   */
  create: (staffData) => {
    if (!staffData || !staffData.name || !staffData.email || !staffData.password) {
      return Promise.reject(new Error('Name, email, and password are required'));
    }
    return api.post('/admin/staff', staffData);
  },

  /**
   * Update staff
   * @param {string|number} id - Staff ID
   * @param {Object} staffData - Updated staff data (all fields optional)
   * @returns {Promise<Object>} Updated staff data
   */
  update: (id, staffData) => {
    if (!id) {
      return Promise.reject(new Error('Staff ID is required'));
    }
    if (!staffData || Object.keys(staffData).length === 0) {
      return Promise.reject(new Error('Staff data is required'));
    }
    return api.put(`/admin/staff/${id}`, staffData);
  },

  /**
   * Update staff status (activate/inactivate)
   * @param {string|number} id - Staff ID
   * @param {'active'|'inactive'} status - Target status
   * @returns {Promise<Object>} Updated staff data
   */
  updateStatus: (id, status) => {
    if (!id) {
      return Promise.reject(new Error('Staff ID is required'));
    }
    if (!status || !['active', 'inactive'].includes(status)) {
      return Promise.reject(new Error('Valid staff status is required'));
    }
    return api.patch(`/admin/staff/${id}/status`, { status });
  },

  /**
   * Delete staff (soft delete)
   * @param {string|number} id - Staff ID
   * @returns {Promise<Object>} Deletion confirmation
   */
  delete: (id) => {
    if (!id) {
      return Promise.reject(new Error('Staff ID is required'));
    }
    return api.delete(`/admin/staff/${id}`);
  },

  /**
   * Get staff statistics overview
   * @returns {Promise<Object>} Staff statistics
   */
  getStats: () => {
    return api.get('/admin/staff/stats/overview');
  },

  /**
   * Get staff insights dashboard data
   * @param {string|number} id - Staff ID
   * @param {Object} params - Query parameters (period: 7d|30d|90d|all)
   * @returns {Promise<Object>} Insights data
   */
  getInsights: (id, params = {}) => {
    if (!id) {
      return Promise.reject(new Error('Staff ID is required'));
    }
    const cleanParams = Object.entries(params).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {});
    const queryParams = new URLSearchParams(cleanParams).toString();
    const endpoint = `/admin/staff/${id}/insights${queryParams ? `?${queryParams}` : ''}`;
    return api.get(endpoint);
  },

  getJobs: (id, params = {}) => {
    if (!id) return Promise.reject(new Error('Staff ID is required'));
    const cleanParams = Object.entries(params).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== '') acc[key] = value;
      return acc;
    }, {});
    const query = new URLSearchParams(cleanParams).toString();
    return api.get(`/admin/staff/${id}/jobs${query ? `?${query}` : ''}`);
  },

  getCompanies: (id, params = {}) => {
    if (!id) return Promise.reject(new Error('Staff ID is required'));
    const cleanParams = Object.entries(params).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== '') acc[key] = value;
      return acc;
    }, {});
    const query = new URLSearchParams(cleanParams).toString();
    return api.get(`/admin/staff/${id}/companies${query ? `?${query}` : ''}`);
  },

  getTickets: (id, params = {}) => {
    if (!id) return Promise.reject(new Error('Staff ID is required'));
    const cleanParams = Object.entries(params).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== '') acc[key] = value;
      return acc;
    }, {});
    const query = new URLSearchParams(cleanParams).toString();
    return api.get(`/admin/staff/${id}/tickets${query ? `?${query}` : ''}`);
  },

  getActivity: (id, params = {}) => {
    if (!id) return Promise.reject(new Error('Staff ID is required'));
    const cleanParams = Object.entries(params).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== '') acc[key] = value;
      return acc;
    }, {});
    const query = new URLSearchParams(cleanParams).toString();
    return api.get(`/admin/staff/${id}/activity${query ? `?${query}` : ''}`);
  },
};

export default staffApi;
